import React, { ReactNode, ForwardedRef, forwardRef, useMemo, useCallback } from 'react';
import { useId } from 'react-aria';
import { isDesktop } from '@direct-frontend/platform-code';
import { DistributiveOmit, genericForwardRef, mergeRefs } from '@direct-frontend/stdlib';
import { BaseRowData } from '@adv-frontend/data-provider';

import { cn } from '../../lib/css/className/className';
import { IconArrowDown as IconArrowBottom } from '../../icons/colorless';
import { Select, SelectColor, SelectWithItemsProps } from '../Select';
import { CommonTrigger } from '../Select/CommonTrigger/CommonTrigger';
import { HStack, VStack } from '../Stack';
import './SelectCompact.css';
import { getSelectTriggerContent } from '../Select';
import { MultiSelectProps, SingleSelectProps } from '../Select';
import { TriggerProps } from '../Select/Trigger/types';
import { TestProps } from '../../internal/types/testTypes';
import { HelpProps } from '../Help';
import { ElementTagProps } from '../TextGeneric/TextGeneric';
import { getSelectTriggerHelp } from '../Select/utils';
import { Stub } from '../Stub';
import { useTheme } from '../../lib/theme/ThemeProvider';
import { useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { SkinColor, SkinRole } from '../../lib/skin/getColorSkin/generated/types';

type CompactProps<T> = DistributiveOmit<T, 'renderTrigger'> &
    TestProps & {
        label?: string;

        /**
         * Компонент со всплывающей подсказкой при клике на знак вопроса.
         * При передаче данного параметра рядом с label рисуется иконка `Help` соответствующего размера.
         * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и
         * выравниванием)
         */
        help?: (helpProps: HelpProps) => ReactNode;
    };

type SingleSelectCompactProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
> = CompactProps<SingleSelectProps<ValueType, SectionType, RowData>>;
type MultiSelectCompactProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
> = CompactProps<MultiSelectProps<ValueType, SectionType, RowData>>;

export type SelectCompactWithItemsProps<
    ValueType extends string,
    SectionType extends string = ValueType,
> = CompactProps<SelectWithItemsProps<ValueType, SectionType>>;

export type SelectCompactProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
> =
    | SingleSelectCompactProps<ValueType, SectionType, RowData>
    | MultiSelectCompactProps<ValueType, SectionType, RowData>;

const cnSelectCompact = cn('SelectCompact');

export type CompactTriggerProps<ValueType extends string> = TriggerProps<ValueType> & {
    label?: string;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с label рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и
     * выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;
};

const skinMap: Record<SelectColor, SkinColor | SkinRole> = {
    contour: 'gray-transparent-contour',
    contrast: 'gray-neutral',
    normal: 'gray-low-contrast',
};

export const SelectCompactTrigger = forwardRef(function SelectCompactTrigger<ValueType extends string>(
    triggerProps: CompactTriggerProps<ValueType>,
    forwardedRef: ForwardedRef<HTMLElement>,
) {
    const {
        value,
        size,
        isDisabled,
        placeholder,
        isOpened,
        label,
        help,
        isTruncated,
        isStub,
        'data-testid': dataTestId = 'SelectCompact',
        color,
    } = triggerProps;

    const { iconSize } = useTheme().sizeTokens[size];
    const textSize = 'current-main';

    const iconLeft = value.length === 1 && value[0]?.iconLeft;
    const { textClasses: iconTextClasses } = useTypographyWithSkin({
        color: 'current',
        size: textSize,
        role: 'placeholder',
    });
    const colorSkinClass = getColorSkin(skinMap[color], {
        isDisabled,
    });
    const { addonClasses } = useInternalVerticalAlignWithSkin({
        textSize: textSize,
        addonType: 'icon',
    });

    const fieldId = useId(triggerProps.id);
    const labelId = useId(triggerProps.labelId);

    // добавление корректных aria-свойств (нужно поскольку используется label за пределами FormField)
    const ariaProps = {
        'aria-labelledby': label ? labelId : undefined,
        'aria-describedby': [triggerProps['aria-describedby'], labelId].filter(Boolean).join(' ') || undefined,
    };

    const renderLabel = useCallback(
        (props: ElementTagProps) => (
            <label
                {...props}
                id={labelId}
                aria-label={label}
                htmlFor={fieldId}
                className={cnSelectCompact(
                    'label',
                    {
                        // Лейбл будет сжиматься после сжатия значения
                        // Лейбл с хелпом не поддержан
                        fullSizeLabel: isTruncated && !help,
                    },
                    [props.className],
                )}
                data-testid={`${dataTestId}.Label`}
            />
        ),
        [labelId, label, fieldId, isTruncated, help, dataTestId],
    );

    const renderValue = useCallback((props: ElementTagProps) => {
        return <span {...props} className={cnSelectCompact('inner-value', [props.className])} />;
    }, []);

    const helpValue = useCallback(
        (helpProps: HelpProps) => {
            return getSelectTriggerHelp(value)?.({ as: 'span', ...helpProps });
        },
        [value],
    );

    const helpWithAdditionalProps = useCallback(
        (helpProps: HelpProps) => {
            // Рендерим кнопку помощи как span, иначе получается невалидная разметка
            // button внутри button
            return help?.({ as: 'span', ...helpProps });
        },
        [help],
    );

    const selectTriggerContent = getSelectTriggerContent(value);

    return (
        <CommonTrigger
            {...triggerProps}
            ref={forwardedRef}
            className={cnSelectCompact({ size }, [colorSkinClass])}
            {...ariaProps}
        >
            {iconLeft && (
                <VStack
                    justifyContent={'center'}
                    alignItems={'center'}
                    className={cnSelectCompact('icon', [addonClasses])}
                >
                    {iconLeft}
                </VStack>
            )}
            <HStack
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                gap={'current-geometry'}
                className={cnSelectCompact('text')}
            >
                <TextGenericWithSkin
                    as={renderLabel}
                    color={'current'}
                    textSize={textSize}
                    skinSize={size}
                    help={helpWithAdditionalProps}
                    isTruncated={isTruncated}
                    role={'placeholder'}
                >
                    {label}
                </TextGenericWithSkin>
                {isStub ? (
                    <VStack className={cnSelectCompact('stub', { size })}>
                        <Stub width={96} height={16} />
                    </VStack>
                ) : (
                    <TextGenericWithSkin
                        className={cnSelectCompact('content')}
                        help={helpValue}
                        textSize={textSize}
                        skinSize={size}
                        color={'current'}
                        textAlign={'right'}
                        isTruncated={isTruncated}
                        as={renderValue}
                        role={selectTriggerContent ? undefined : 'placeholder'}
                    >
                        {selectTriggerContent || placeholder}
                    </TextGenericWithSkin>
                )}
            </HStack>
            <VStack className={cnSelectCompact('icon-right-wrapper', [addonClasses])}>
                <IconArrowBottom
                    size={iconSize}
                    className={cnSelectCompact('icon-right', { opened: isOpened }, [iconTextClasses])}
                />
            </VStack>
        </CommonTrigger>
    );
});

export const SelectCompact = genericForwardRef(function SelectCompact<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(props: SelectCompactProps<ValueType, SectionType, RowData>, forwardedRef: ForwardedRef<HTMLElement>) {
    const { label, help } = props;

    const renderTrigger = useMemo(
        () =>
            forwardRef<HTMLElement, TriggerProps<ValueType>>(function RenderTrigger(triggerProps, ref) {
                return (
                    <SelectCompactTrigger
                        {...triggerProps}
                        ref={mergeRefs(forwardedRef, ref)}
                        label={label}
                        help={help}
                    />
                );
            }),
        [forwardedRef, label, help],
    );

    const renderDataProviderTrigger = useMemo(
        () =>
            forwardRef<HTMLElement, TriggerProps<string>>(function RenderTrigger(triggerProps, ref) {
                return (
                    <SelectCompactTrigger
                        {...triggerProps}
                        ref={mergeRefs(forwardedRef, ref)}
                        label={label}
                        help={help}
                    />
                );
            }),
        [forwardedRef, help, label],
    );

    const resultProps =
        'dataProvider' in props
            ? {
                  ...props,
                  renderTrigger: isDesktop() ? renderDataProviderTrigger : undefined,
              }
            : {
                  ...props,
                  renderTrigger: isDesktop() ? renderTrigger : undefined,
              };

    return <Select {...resultProps} aria-label={typeof label === 'string' ? label : props['aria-label']} />;
});
